import { FC } from "react"

/** Components */
import styled from "styled-components"
import { FilterBannerLabelGetter } from "../../../filters/FilterBanner/FilterBannerLabelGetter"
import { iMutableFilterState, tFilterContext, tFilterDef } from "../../../filters/types"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import { RowNode } from "ag-grid-community"
import { colorAttentionBlue10, colorAttentionBlue50, colorFunctionalWhite, colorMiscGray80, colorMiscGray90, spacingL, spacingS, spacingXs } from "@rhumbix/rmbx_design_system_web"

/** Types */
type Props = {
    data: Record<string, any>
    config: Record<string, any>
    context: tFilterContext
    node: RowNode
    weeklyViewFilters: tFilterDef[]
}

const FiltersCellRenderer: FC<Props> = props => {
    const filters: iMutableFilterState = {}
    Object.entries(props.data.filters).forEach(([queryParam, value]) => {
        // Find the filter for this key
        const filter = props.weeklyViewFilters.find(f => f.parameterName === queryParam)
        if (!filter) {
            return
        }
        const key: keyof iMutableFilterState = filter.key
        filters[key] = value as any
    })
    const filterMap = FilterBannerLabelGetter({
        filterState: filters,
        filterDefs: props.weeklyViewFilters as Array<tFilterDef>,
        context: props.context as tFilterContext,
        noDomElements: true,
    }) as Array<{ label: string; value: string }>

    const tooltipSpot = document.getElementById("master-container")

    const getFilterElem = (idx: number, label: string, value: string, isTooltip = false) => {
        const key = isTooltip ? `filter-tooltip-${idx}` : `filter-${idx}`
        const OuterElem = isTooltip ? TooltipFilter : Filter
        const LabelElem = isTooltip ? TooltipFilterLabel : FilterLabel
        return (
            <OuterElem key={key}>
                <LabelElem>{label}:</LabelElem>
                <FilterValue style={isTooltip ? { width: "fit-content" } : {}}>{value}</FilterValue>
            </OuterElem>
        )
    }

    return (
        <>
            <FilterHolder data-tooltip-id={`filters-tooltip-${props.node.id}`}>{filterMap.map((obj, idx) => getFilterElem(idx, obj.label, obj.value, false))}</FilterHolder>
            <Portal container={tooltipSpot}>
                <ReactTooltip
                    id={`filters-tooltip-${props.node.id}`}
                    place="bottom"
                    opacity={0.95}
                    style={{
                        zIndex: 10000,
                        maxWidth: 500,
                        display: "block",
                        backgroundColor: colorMiscGray90,
                    }}
                    render={() => <TooltipFilterHolder>{filterMap.map((obj, idx) => getFilterElem(idx, obj.label, obj.value, true))}</TooltipFilterHolder>}
                />
            </Portal>
        </>
    )
}

export default FiltersCellRenderer

const FilterHolder = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${colorAttentionBlue50};
    display: block;
`

const TooltipFilterHolder = styled.div`
    gap: ${spacingS};
    height: 40px;
    display: flex;
    flex-flow: wrap;
    max-width: 500px;
    height: auto;
`

const TooltipFilter = styled.span`
    padding: ${spacingS};
    background-color: ${colorMiscGray80};
    color: ${colorFunctionalWhite};
    height: auto;
    display: flex;
    width: fit-content;
`

const Filter = styled.span`
    padding: ${spacingS};
    background-color: ${colorAttentionBlue10};
    color: ${colorAttentionBlue50};
    height: ${spacingL};
    display: inline-flex;
    margin-right: ${spacingS};
`

const FilterLabel = styled.span`
    margin-right: ${spacingXs};
    font-weight: 500;
`

const TooltipFilterLabel = styled(FilterLabel)`
    width: fit-content;
    white-space: nowrap;
    align-self: center;
`

const FilterValue = styled.span`
    font-weight: normal;
`
